/**
 * Pure number-formatting helpers, extracted from utilities.ts so they can be
 * unit-tested (utilities.ts reads import.meta at module scope, which jest
 * cannot parse). utilities.ts re-exposes these under the original names with
 * the env-derived locale applied.
 */

export function formatToMaxFourDigitsWithLocale(
  num: number | string,
  isEconomics: boolean,
  locale: string
): string {
  if (isEconomics) {
    if (num === null || num === undefined || num === '-.-' || num === '') {
      return '-.-';
    }
    if (num === 0 || num === '0') {
      return parseFloat('0').toLocaleString(locale, {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
      });
    }
    return parseFloat('' + num + '').toLocaleString(locale, {
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
    });
  }

  let str = String(num).replace(',', '.');
  let negative = false;
  if (str.startsWith('-')) {
    negative = true;
    str = str.slice(1);
  }
  const [intPart, decPart = ''] = str.split('.');

  // Special case: exact zero should be formatted as 0.00 (locale-aware)
  const numericValue = Number(str === '' ? '0' : str);
  if (!Number.isNaN(numericValue) && numericValue === 0) {
    const localized = numericValue.toLocaleString(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return (negative ? '-' : '') + localized;
  }

  if (intPart.length >= 4) {
    // CM-5110 #1: format the FULL integer part — slicing to the first four
    // characters rendered 5+ digit values 10-100x too small (10000 -> 1'000).
    const formattedResult =
      (negative ? '-' : '') +
      Number(intPart).toLocaleString(locale, {
        maximumFractionDigits: 0,
      });
    if (formattedResult.includes('NaN')) {
      return '-.-';
    }
    return formattedResult;
  }

  const allowedDecimals = 4 - intPart.length;
  const trimmedDec = decPart.slice(0, allowedDecimals);
  // Remove trailing zeros from the decimal part (e.g., 1.230 -> 1.23, 1.200 -> 1)
  const trimmedDecNoZeros = trimmedDec.replace(/0+$/, '');

  let result = intPart;
  if (trimmedDecNoZeros.length > 0) {
    result += '.' + trimmedDecNoZeros;
  }

  const numResult = Number(result);
  if (isNaN(numResult)) {
    return '-.-';
  }

  try {
    const fractionDigits = trimmedDecNoZeros.length;
    return (
      (negative ? '-' : '') +
      (numResult || 0).toLocaleString(locale, {
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
      })
    );
  } catch (err: any) {
    console.warn(err);
    return '-.-';
  }
}

/**
 * FLEXO-6133 #2: the " | <value> <unit>" suffix on stats-overview breakdown
 * rows. Always formats from the raw NUMBER — never compare or re-format an
 * already-localized string (a de-CH thousands separator coerces it to NaN,
 * which is what collapsed every MWh-scale row to " | 0.00").
 */
export function energyBreakdownSuffixWithLocale(
  valueNumber: number | null | undefined,
  unit: string,
  locale: string
): string {
  // A KPI absent from the payload (null/undefined) is not a zero: render the
  // unavailable marker instead of fabricating "0.00" (FLEXO-6133 #2 related).
  if (valueNumber === null || valueNumber === undefined || Number.isNaN(valueNumber)) {
    return ` | -.-${unit ? ` ${unit}` : ''}`;
  }
  const formatted = formatToMaxFourDigitsWithLocale(valueNumber, false, locale);
  return ` | ${formatted}${unit ? ` ${unit}` : ''}`;
}
